import { ImageOff, Maximize2 } from "lucide-react";
import { useState, type ImgHTMLAttributes, type ReactNode } from "react";

import { ArtworkPreviewModal } from "./ArtworkPreviewModal";
import { SelectionOverlay } from "./SelectionOverlay";
import { UploadProgressOverlay } from "./UploadProgressOverlay";

export type ImagePlaceholderProps = {
  children?: ReactNode;
  className?: string;
  isLoading?: boolean;
  isUploading?: boolean;
  startTime?: number | undefined;
  imageSrc?: string;
  imageAlt?: string;
  isSelected?: boolean;
  onSelect?: () => void;
  onEdit?: () => void;
  showEditButton?: boolean;
  crossOrigin?: ImgHTMLAttributes<HTMLImageElement>["crossOrigin"];
  hoverAlignment?: "center" | "bottom";
  isRefineMode?: boolean;
};

export function ImagePlaceholder({
  children,
  className = "",
  isLoading = false,
  isUploading = false,
  startTime,
  imageSrc,
  imageAlt = "",
  isSelected = false,
  onSelect,
  onEdit,
  showEditButton = false,
  crossOrigin,
  hoverAlignment = "bottom",
  isRefineMode = false
}: ImagePlaceholderProps) {
  const [hasError, setHasError] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleImageError = () => {
    setHasError(true);
  };

  const getImageOpacity = () => {
    if (isUploading) return "opacity-60";
    if (isSelected) return "opacity-80";
    return "opacity-100 group-hover:opacity-80";
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };

  // Generation loading state
  if (isLoading) {
    return (
      <div
        className={`relative aspect-square overflow-hidden rounded-lg bg-gray-200 before:absolute before:inset-0 before:left-1/2 before:-ml-[250%] before:w-[500%] before:animate-[shimmer_0.8s_linear_infinite] before:bg-gradient-to-r before:from-transparent before:from-[46%] before:via-white/35 before:via-[50%] before:to-transparent before:to-[54%] before:content-[''] ${className}`}
      />
    );
  }

  // Show error state when image fails to load
  if (imageSrc && hasError) {
    return (
      <div
        className={`relative aspect-square min-h-0 min-w-0 overflow-hidden rounded-lg border border-gray-200 ${className} box-border`}
      >
        <div className="absolute inset-px box-border flex items-center justify-center rounded-lg bg-white">
          <div className="box-border flex w-3/4 flex-col items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 py-3 text-center shadow-sm">
            <ImageOff className="size-6 text-gray-400" />
            <p className="leading-4.5 hidden px-1 text-center text-xs font-normal text-gray-600 xl:block">
              Oops, this one didn&apos;t work
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-cta-gradient ${isSelected || !onSelect ? "" : "group"} relative aspect-square min-h-0 min-w-0 overflow-hidden rounded-lg ${className}`}
    >
      <div className="absolute inset-px flex items-center justify-center rounded-lg bg-white">
        {imageSrc ? (
          <>
            <img
              src={imageSrc}
              alt={imageAlt}
              onError={handleImageError}
              className={`size-full rounded-lg object-cover transition-opacity duration-200 ${getImageOpacity()}`}
              {...(crossOrigin && { crossOrigin })}
            />

            {onSelect && (
              <SelectionOverlay
                isSelected={isSelected}
                onSelect={onSelect}
                {...(onEdit && { onEdit })}
                showEditButton={showEditButton}
                hoverAlignment={hoverAlignment}
                isRefineMode={isRefineMode}
              />
            )}

            <button
              onClick={handleExpandClick}
              className="absolute left-1 top-1 z-20 flex size-8 cursor-pointer items-center justify-center rounded-sm border border-gray-400 bg-white backdrop-blur-sm transition-all hover:scale-105"
              aria-label="Preview artwork"
              data-testid="expand-artwork-button"
            >
              <Maximize2 className="size-6 text-gray-700" />
            </button>

            {isUploading && <UploadProgressOverlay startTime={startTime} />}
          </>
        ) : (
          children
        )}
      </div>

      {/* Artwork Preview Modal */}
      {imageSrc && (
        <ArtworkPreviewModal
          isOpen={isPreviewOpen}
          onClose={handleClosePreview}
          imageSrc={imageSrc}
          imageAlt={imageAlt}
          onSelect={onSelect}
          isSelected={isSelected}
        />
      )}
    </div>
  );
}
